import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AdminAuthService } from '../../core/services/auth.service';

interface NavItem {
  label: string;
  route: string;
  icon: string;
  exact: boolean;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

@Component({
  selector: 'app-admin-layout',
  templateUrl: './admin-layout.component.html',
})
export class AdminLayoutComponent implements OnInit, OnDestroy {
  pageTitle = 'Overview';

  readonly navSections: NavSection[] = [
    {
      label: 'Operations',
      items: [
        { label: 'Overview', route: '/', icon: 'layout-dashboard', exact: true },
        { label: 'Users', route: '/users', icon: 'users', exact: false },
        { label: 'Subscriptions', route: '/subscriptions', icon: 'credit-card', exact: false },
        { label: 'Integrations', route: '/integrations', icon: 'zap', exact: false },
        { label: 'Monitoring', route: '/monitoring', icon: 'activity', exact: false },
        { label: 'Tiers', route: '/tiers', icon: 'layers', exact: false },
        { label: 'Bug Reports', route: '/bug-reports', icon: 'bug', exact: false },
      ],
    },
    {
      label: 'Observability',
      items: [
        { label: 'Findings', route: '/observability/findings', icon: 'scan-search', exact: false },
      ],
    },
    {
      label: 'System',
      items: [
        { label: 'Monitoring Rules', route: '/settings/monitoring', icon: 'settings-2', exact: false },
        { label: 'System Logs',      route: '/logs',                icon: 'scroll-text', exact: false },
        { label: 'Settings',         route: '/settings',            icon: 'settings',    exact: false },
      ],
    },
  ];

  private readonly routeTitles: Record<string, string> = {
    '/': 'Overview',
    '/users': 'User Management',
    '/subscriptions': 'Subscriptions',
    '/integrations': 'Integrations',
    '/monitoring': 'Monitoring',
    '/tiers': 'Tier Management',
    '/bug-reports': 'Bug Reports',
    '/observability/findings': 'Findings',
    '/settings/monitoring': 'Monitoring Rules',
    '/logs': 'System Logs',
    '/settings': 'Settings',
  };

  readonly displayName$ = this.auth.displayName$;
  readonly userInitials$ = this.auth.userInitials$;

  private routerSub?: Subscription;

  constructor(
    private readonly auth: AdminAuthService,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.routerSub = this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(e => {
        const url = (e as NavigationEnd).urlAfterRedirects;
        this.pageTitle = this.routeTitles[url] ?? 'Admin';
      });
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
  }

  async signOut(): Promise<void> {
    await this.auth.signOut();
    await this.router.navigate(['/login']);
  }
}
